import { translate as t } from '@/lib/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ApiError, identitiesApi } from '@/api';
import { accountKeys, identityKeys } from '@/lib/queryKeys';
import type { IdentityUpsertRequest } from '@/types/api';

export function useCreateIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IdentityUpsertRequest) => identitiesApi.create(body),
    onSuccess: () => {
      toast.success(t('Identité créée', 'Identity created'));
      qc.invalidateQueries({ queryKey: identityKeys.all });
    },
    onError: (err) => {
      const apiErr = ApiError.from(err);
      if (apiErr.isConflict()) {
        toast.error(t('Ce nom est déjà utilisé.', 'This name is already in use.'));
      } else {
        toast.error(t("Impossible de créer l'identité", 'Unable to create identity'), {
          description: apiErr.localizedMessage,
        });
      }
    },
  });
}

export function useUpdateIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<IdentityUpsertRequest> }) =>
      identitiesApi.update(id, body),
    onSuccess: () => {
      toast.success(t('Identité mise à jour', 'Identity updated'));
      qc.invalidateQueries({ queryKey: identityKeys.all });
    },
    onError: (err) => {
      const apiErr = ApiError.from(err);
      if (apiErr.isConflict()) {
        toast.error(t('Ce nom est déjà utilisé.', 'This name is already in use.'));
      } else {
        toast.error(t('Échec de la mise à jour', 'Update failed'), {
          description: apiErr.localizedMessage,
        });
      }
    },
  });
}

export function useDeleteIdentity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => identitiesApi.remove(id),
    onSuccess: () => {
      toast.success(t('Identité supprimée', 'Identity deleted'));
      qc.invalidateQueries({ queryKey: identityKeys.all });
      qc.invalidateQueries({ queryKey: accountKeys.all });
    },
    onError: (err) => {
      const apiErr = ApiError.from(err);
      if (apiErr.isConflict()) {
        toast.error(t('Suppression impossible', 'Unable to delete'), {
          description: t(
            'Des comptes sont encore rattachés à cette identité.',
            'Accounts are still linked to this identity.',
          ),
        });
      } else {
        toast.error(t('Suppression impossible', 'Unable to delete'), {
          description: apiErr.localizedMessage,
        });
      }
    },
  });
}
